import type { Role } from '@kia/shared';
import { canViewFor, type View } from '../nav/navModel';
import type { FocusKind } from '../nav/jumpTarget';

/**
 * Where a business notification should take you (E4-01). Maps the `ref_type` the server emits to the screen
 * that shows that record — and, where the `ref_id` identifies a record, to the record itself (CW-10). Pure +
 * role-aware so it's unit-testable and can't send a user to a screen they can't open. Shares the same
 * `{ view, focus, focusId }` deep-link contract as global search (jumpTarget), so a click from the bell and a
 * click from search land the SAME way; the shell applies the focus with one shared handler.
 *
 * `thread`/`broadcast` are deliberately NOT here — the messaging drawer handles those directly in the bell.
 */
const REF_VIEW: Record<string, View> = {
  deal: 'deals',
  cheque: 'deals',
  release: 'release',
  prices: 'prices',
  accounting: 'ledger',
  assumptions: 'settings',
  // `cash` = the cheque-due-soon digest (CW-10): an AGGREGATE of receivable cheques across many deals, so it
  // can't focus one record — it lands on the cash calendar, the collection workspace. (Was refType:null → a
  // dead click.)
  cash: 'cash',
  // Aggregate risk digests (TKT-3): one bell for the whole margin-at-risk / overdue-tranche portfolio lands
  // on the dashboard where those worklist cards live and each deal is clickable — not a per-deal focus.
  margin_risk: 'dash',
  tranche_risk: 'dash',
};

export interface NotificationTarget {
  view: View;
  /** the record kind to deep-focus (mirrors search's FocusKind); absent ⇒ land on the screen only. */
  focus?: FocusKind;
  /** the record id for `focus`. */
  focusId?: number;
  /** a sub-tab to open on a tabbed screen (settings/prices), e.g. the assumptions «فرض ماهانه» cost tab. */
  focusTab?: string;
}

/**
 * Resolve a notification's navigation target, or null to just mark-read. Returns null when: the type is
 * unknown/absent, the id is missing, OR the destination screen isn't visible to this role (the degrade — a
 * non-Finance user tapping an `accounting` alert stays put, no error).
 */
export function resolveNotificationTarget(
  refType: string | null,
  refId: string | null,
  role: Role,
): NotificationTarget | null {
  if (!refType || !refId) return null;
  const view = REF_VIEW[refType];
  if (!view) return null;
  if (!canViewFor(role, view)) return null;

  // A `deal`/`release` ref carries the record id → deep-focus it (the plumbing exists in the shell). The
  // aggregate digests (cheque→cash, margin_risk/tranche_risk→dash) and screen-only types just navigate.
  if (refType === 'deal' || refType === 'release') {
    const focusId = Number(refId);
    if (!Number.isFinite(focusId)) return null; // a record alert with no valid id is unroutable
    return { view, focus: refType, focusId };
  }
  // An assumptions alert opens the cost sub-tab of Settings («فرض ماهانه»), not the last-used one.
  if (refType === 'assumptions') return { view, focusTab: 'cost' };
  return { view };
}
